import { httpClient } from './api-client';
import type { ClientAuthScope } from '@/models/auth';

interface AuthScopeResponse {
  scopes: Array<{
    scopeKey: string;
    appKind: string;
    role: string;
    tenantId?: string;
    tenantName?: string;
    organizationName?: string;
    label: string;
    isDefault: boolean;
  }>;
  suggestedScopeKey?: string;
}

function normalizeScopeResponse(response: AuthScopeResponse | Record<string, unknown> | null | undefined): {
  scopes: AuthScopeResponse['scopes'];
  suggestedScopeKey?: string;
} {
  const rawScopes = Array.isArray(response?.scopes)
    ? response.scopes
    : Array.isArray((response as { Scopes?: unknown } | null | undefined)?.Scopes)
      ? ((response as { Scopes: AuthScopeResponse['scopes'] }).Scopes)
      : [];

  const suggestedScopeKey =
    typeof response?.suggestedScopeKey === 'string'
      ? response.suggestedScopeKey
      : typeof (response as { SuggestedScopeKey?: unknown } | null | undefined)?.SuggestedScopeKey === 'string'
        ? (response as { SuggestedScopeKey: string }).SuggestedScopeKey
        : undefined;

  return {
    scopes: rawScopes,
    suggestedScopeKey,
  };
}

export async function getClientScopes(): Promise<{
  scopes: ClientAuthScope[];
  suggestedScopeKey?: string;
}> {
  const response = await httpClient.get<AuthScopeResponse | Record<string, unknown>>('/api/v1/auth/scopes');
  const normalized = normalizeScopeResponse(response);

  const scopes = normalized.scopes
    .filter(
      (scope): scope is AuthScopeResponse['scopes'][number] & { role: 'admin' | 'reviewer' | 'viewer'; appKind: 'client'; tenantId: string } =>
        scope.appKind === 'client'
        && (scope.role === 'admin' || scope.role === 'reviewer' || scope.role === 'viewer')
        && typeof scope.tenantId === 'string'
        && scope.tenantId.length > 0,
    )
    .map((scope) => ({
      scopeKey: scope.scopeKey,
      appKind: 'client' as const,
      role: scope.role,
      tenantId: scope.tenantId,
      tenantName: scope.tenantName,
      organizationName: scope.organizationName,
      label: scope.label,
      isDefault: scope.isDefault,
    }));

  return {
    scopes,
    suggestedScopeKey: normalized.suggestedScopeKey,
  };
}
